import React from "react";
import {
  ASSESSMENT_SUPERCATEGORY,
  ASSESSMENT_SUPERCATEGORY_TYPES,
  ASSESSMENT_TYPE_TO_TITLE,
} from "@sscs/report/constants";
import { ReportDetails, RiskSuperCategory, SDLCMetadata } from "@sscs/types";
import { RiskStatusIcon, Section } from "@sscs/ui";
import { getClassName } from "@sscs/utils";
import { LinkToReport } from "Components";
import { compareCategoryStatus } from "../../utils";
import styles from "./categoryGroups.module.scss";

const SUPER_CATEGORIES: readonly RiskSuperCategory[] = [
  "compliance",
  "security",
  "threats",
] as const;

type AssessmentSentenceProps = {
  id: string;
  assessment: SDLCMetadata.risk_assessment_t;
  category: SDLCMetadata.assessment_type_t;
  report: ReportDetails;
  className?: string;
};

const AssessmentSentence = ({
  id,
  assessment,
  category,
  report,
  className,
}: AssessmentSentenceProps): React.JSX.Element => (
  <span className={className}>
    {assessment.count !== 0 && assessment.enabled && (
      <>
        <LinkToReport
          id={id}
          report={{
            ...report,
            riskCategory: category,
            riskViolations: assessment.violations,
          }}
        >
          {assessment.count.toLocaleString()}
        </LinkToReport>{" "}
      </>
    )}
    {assessment.label}
  </span>
);

type CategoryGroupsProps = {
  id: string;
  current: SDLCMetadata.risk_object_t;
  reportDetails: ReportDetails;
  previous?: SDLCMetadata.risk_object_t;
  referenceReportDetails?: ReportDetails;
  deletedReference?: boolean;
};

const CategoryGroups = ({
  id,
  current,
  reportDetails,
  previous,
  referenceReportDetails,
  deletedReference = false,
}: CategoryGroupsProps): React.JSX.Element => (
  <>
    {SUPER_CATEGORIES.map((superCategory) => (
      <Section key={superCategory} className={styles["section-grid"]}>
        <Section.Title as="h3" className={styles["section-title-row"]}>
          {ASSESSMENT_SUPERCATEGORY[superCategory]}
        </Section.Title>
        <Section.Content className={styles["section-content-grid"]}>
          {ASSESSMENT_SUPERCATEGORY_TYPES[superCategory].map((category) => {
            const currentAssessment = current[category];
            const previousAssessment = previous?.[category];
            const isDisabled =
              !currentAssessment.enabled &&
              (previousAssessment === undefined || !previousAssessment.enabled);
            const comparison =
              previousAssessment && !isDisabled
                ? compareCategoryStatus(currentAssessment, previousAssessment)
                : undefined;

            return (
              <div
                key={category}
                className={getClassName(
                  styles.row,
                  styles[`status-${currentAssessment.status}`],
                  { [styles.disabled]: isDisabled },
                )}
              >
                <RiskStatusIcon
                  status={currentAssessment.status}
                  disabled={isDisabled}
                />
                <span className={styles["category-name"]}>
                  {ASSESSMENT_TYPE_TO_TITLE[category]}
                  {comparison !== undefined &&
                    (comparison === "unchanged" ? (
                      <span className={styles["no-change"]}>no change</span>
                    ) : (
                      <span
                        className={getClassName(
                          styles.chip,
                          styles[`chip-${currentAssessment.status}`],
                        )}
                      >
                        {currentAssessment.status}{" "}
                        <span className={styles["chip-vs"]}>vs</span>{" "}
                        {previousAssessment?.status}
                      </span>
                    ))}
                </span>
                <AssessmentSentence
                  id={`current-${category}-${id}`}
                  assessment={currentAssessment}
                  category={category}
                  report={reportDetails}
                  className={styles.sentence}
                />
                {previousAssessment && (
                  <>
                    <span className={styles.vs}>vs</span>
                    <AssessmentSentence
                      id={`previous-${category}-${id}`}
                      assessment={previousAssessment}
                      category={category}
                      report={referenceReportDetails ?? reportDetails}
                      className={getClassName(styles.previous, {
                        [styles.deleted]: deletedReference,
                      })}
                    />
                  </>
                )}
              </div>
            );
          })}
        </Section.Content>
      </Section>
    ))}
  </>
);

export default CategoryGroups;
